import React, { useEffect, useState } from 'react';
import './teamsPlanning.css';
import SummaryCards from './SummaryCards';
import WorkloadBalanceCard from './WorkloadBalanceCard';
import NonProductiveRatesCard from './NonProductiveRatesCard';
import PendingDeclarationsCard from './PendingDeclarationsCard';
import ResourceDriftCard from './ResourceDriftCard';
import TeamsPlanningSkeleton from './TeamsPlanningSkeleton';
import { formatApiError, getTeamsPlanningStats } from '../../services/api';
import { setCategoryDisplayCache } from '../planning/categoryCache';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../i18n';

export default function TeamsPlanningPage({
  filters,
  onFilterOptionsChange,
  refreshKey = 0,
}) {
  const { t } = useTranslation('planning');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getTeamsPlanningStats(filters || {});
        if (cancelled) return;
        if (Array.isArray(data?.categories)) {
          setCategoryDisplayCache(data.categories);
        }
        setStats(data);
        if (data?.filter_options && onFilterOptionsChange) {
          onFilterOptionsChange(data.filter_options);
        }
      } catch (err) {
        if (cancelled) return;
        const formatted = formatApiError(err);
        setError(formatted.generalError || t('failedToLoadTeamsPlanning'));
        setStats(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // Intentionally omit onFilterOptionsChange from deps to avoid refetch loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters?.date_from,
    filters?.date_to,
    filters?.team,
    filters?.member,
    filters?.centre,
    filters?.hours_per_day,
    filters?.working_days,
    refreshKey,
  ]);

  const refreshedLabel = stats?.refreshed_at
    ? t('dataRefreshed', { time: new Date(stats.refreshed_at).toLocaleTimeString(getDateLocale()) })
    : t('waitingForData')

  return (
    <div className="teams-planning-page flex flex-col min-h-full bg-background">
      <div className="space-y-4">
        {loading && <TeamsPlanningSkeleton />}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">
            {error}
          </div>
        )}
        {!loading && !error && (
          <>
            <div className="tp-reveal tp-reveal-1">
              <SummaryCards
                summary={stats?.summary}
                declarationRate={stats?.declaration_rate}
                categories={stats?.categories}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-4 tp-reveal tp-reveal-2">
              <WorkloadBalanceCard
                workloadBalance={stats?.workload_balance}
                categories={stats?.categories}
              />
              <NonProductiveRatesCard
                rates={stats?.non_productive_rates}
                totalHours={stats?.total_hours}
                categories={stats?.categories}
              />
            </div>

            <div className="tp-reveal tp-reveal-3">
              <ResourceDriftCard
                resourceDrift={stats?.resource_drift}
                theoretical={stats?.capacity?.theoretical}
              />
            </div>

            <div className="tp-reveal tp-reveal-4">
              <PendingDeclarationsCard filters={filters} />
            </div>

            <footer className="pt-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 text-text-secondary border-t border-border tp-reveal tp-reveal-5">
              <p className="text-xs font-medium">{refreshedLabel}</p>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
